import { useState, useEffect } from 'react';
import { PLANBASE_CONFIG } from '../config';

export interface Plan {
  id: string;
  name: string;
  description: string;
  price: string;
  interval: 'month' | 'year';
  currency: string;
  stripe_price_id: string;
  stripe_product_id: string;
  features: string[];
  is_active: boolean;
  promotion_code?: string;
  stripe_promotion_code_id?: string;
  trial_period_days?: number;
}

interface UsePlansResult {
  plans: Plan[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePlans(): UsePlansResult {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${PLANBASE_CONFIG.apiUrl}/sdk/plans?project_id=${PLANBASE_CONFIG.projectId}`,
        {
          headers: {
            'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
            'X-Project-ID': PLANBASE_CONFIG.projectId,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Parse features JSON string to array
        const parsedPlans = (data.plans || []).map((plan: any) => ({
          ...plan,
          features: typeof plan.features === 'string'
            ? JSON.parse(plan.features)
            : plan.features,
        }));
        // Deduplicate by ID in case the API returns duplicates
        const seen = new Set<string>();
        const uniquePlans = parsedPlans.filter((p: Plan) => {
          if (seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        });
        setPlans(uniquePlans);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch plans');
      }
    } catch (err) {
      console.error('[usePlans] Error fetching plans:', err);
      setError('Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  return {
    plans,
    loading,
    error,
    refetch: fetchPlans,
  };
}
